import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Bot, Shield, BarChart2, Globe, Layers, ArrowRight, ArrowLeft,
  TrendingUp, Users, Cpu, Activity, Sparkles, Zap, Target,
  MessageCircle, Award, Crown, Lock, Wallet, Bell, Eye,
  BarChart, PieChart, GitBranch, Radio, Search, FileText,
  Flame, ThumbsUp, Hash, Star, Megaphone, UserCheck, Vote,
  ChevronRight, Play, RefreshCw, AlertTriangle, Gauge,
  ShieldCheck, Crosshair, Pause, Timer, Copy, Boxes,
  Receipt, Building2, CheckCircle, Compass, DollarSign,
  Mic, Send, Webhook, ScanLine, BookOpen, BadgeCheck,
  FlaskConical, ClipboardCheck, Radar, ShieldAlert, Brain, Droplets,
  LayoutGrid, ArrowDownUp
} from 'lucide-react';
import logo from '../assets/images/haythix-logo.png';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import Seo from '../components/Seo';
import { PLATFORM_FEATURE_COUNT } from '../data/platformStats';

// ─── Feature categories ─────────────────────────────────────────────────────

const CATEGORIES = [
  {
    id: 'trading',
    label: 'Trading & Bots',
    icon: Bot,
    gradient: 'from-amber-400 to-orange-500',
    bgGlow: 'bg-amber-400/5',
    borderGlow: 'hover:border-amber-400/30 hover:shadow-amber-400/10',
    description: 'Institutional-grade trading automation across chains',
    features: [
      {
        icon: Bot, title: 'Multi-Bot Automation',
        desc: 'Run up to 10 fully independent bots simultaneously. Each bot manages its own strategies, wallets, and risk parameters — like having 10 traders working for you 24/7.',
        highlight: 'Up to 10 bots'
      },
      {
        icon: Wallet, title: '10 Wallets Per Bot',
        desc: 'Each bot can operate across 10 isolated wallets with independent position management. Spread risk, test strategies, and scale without correlation.',
        highlight: 'Industry first'
      },
      {
        icon: TrendingUp, title: 'DCA & Grid Bots',
        desc: 'Dollar-cost averaging and grid trading strategies with configurable intervals, price ranges, and take-profit levels. Set once, profit repeatedly.',
        highlight: 'Set & forget'
      },
      {
        icon: Boxes, title: 'Smart Trade Terminal',
        desc: 'Combined entry + up to 5 take-profit ladders + trailing stop-loss as one logical position. OCO logic, break-even-after-first-TP, and real-time peak tracking.',
        highlight: 'Multi-TP ladder'
      },
      {
        icon: GitBranch, title: 'Conditional / Composite Bots',
        desc: 'Chain market regimes to strategies — "if BTC trends up run momentum, else if ranging run grid, else pause." Real OHLCV regime detection every 5 minutes.',
        highlight: 'Regime-aware'
      },
      {
        icon: Layers, title: 'Algo Orders (TWAP/VWAP/Iceberg/Bracket)',
        desc: 'Institutional execution algorithms previously locked behind Bloomberg terminals. Hide order size, time-slice large orders, or chain entry+TP+SL atomically.',
        highlight: 'Institutional-grade'
      },
      {
        icon: RefreshCw, title: 'Cross-Exchange Arbitrage',
        desc: 'Real-time price difference detection across DEX and CEX platforms. Net profit calculator factors in gas fees, bridge costs, and slippage before execution.',
        highlight: 'Real-time'
      },
      {
        icon: Radio, title: 'Signal Bot (TradingView)',
        desc: 'Connect TradingView alerts directly to your bots. When your indicators fire, haythix executes instantly — no manual intervention needed.',
        highlight: 'Auto-execute'
      },
      {
        icon: Copy, title: 'Bot Cloning',
        desc: 'Duplicate any bot configuration with one click. Perfect for deploying the same strategy across multiple tokens or chains without manual setup.',
        highlight: '1-click clone'
      },
      {
        icon: BarChart2, title: 'Strategy Marketplace',
        desc: 'Access 15+ professionally audited strategies or publish your own. Subscribe to top-performing bots and auto-copy their trades.',
        highlight: '15+ strategies'
      },
      {
        icon: Play, title: 'Paper Trading',
        desc: 'Test any strategy with simulated funds before risking real capital. Full performance tracking, P&L reporting, and strategy validation.',
        highlight: 'Zero risk testing'
      },
    ]
  },
  {
    id: 'intelligence',
    label: 'AI & Intelligence',
    icon: Cpu,
    gradient: 'from-purple-400 to-violet-600',
    bgGlow: 'bg-purple-400/5',
    borderGlow: 'hover:border-purple-400/30 hover:shadow-purple-400/10',
    description: 'haythix AI — your edge in every market condition',
    features: [
      {
        icon: Cpu, title: 'AI Trading Signals',
        desc: 'Real-time Buy, Sell, and Hold signals powered by haythix AI. Multi-timeframe analysis with confidence scores and entry/exit price targets.',
        highlight: 'Real-time signals'
      },
      {
        icon: Sparkles, title: 'AI Strategy Generator',
        desc: 'Describe your trading idea in plain English — haythix AI builds the complete strategy with entry/exit rules, position sizing, and risk parameters.',
        highlight: 'Natural language'
      },
      {
        icon: Activity, title: 'Market Regime Classification',
        desc: 'Automatically detects trending, ranging, volatile, or calm market conditions. Your bots adapt their behavior in real-time to match the current regime.',
        highlight: 'Auto-adapt'
      },
      {
        icon: BarChart, title: 'Monte Carlo Simulations',
        desc: 'Run thousands of probabilistic scenarios on your strategy before deploying real capital. Understand worst-case drawdowns and expected returns.',
        highlight: '10,000 scenarios'
      },
      {
        icon: PieChart, title: 'Bayesian Probability Engine',
        desc: 'Continuously updating probability models that learn from market data. More trades = smarter predictions. Your personal edge that sharpens over time.',
        highlight: 'Self-learning'
      },
      {
        icon: Search, title: 'Pattern Recognition',
        desc: 'Detects chart patterns, support/resistance levels, and volume anomalies across all your tracked tokens. Alerts you before breakouts happen.',
        highlight: 'Pre-breakout alerts'
      },
      {
        icon: Eye, title: 'Anomaly Detection',
        desc: 'Flags unusual trading activity, whale movements, and sudden volume spikes. Know when something is happening before the crowd notices.',
        highlight: 'haythix exclusive'
      },
      {
        icon: TrendingUp, title: 'AI Market Forecasts',
        desc: 'Multi-model ensemble forecasting with confidence intervals. Short-term (1-24h), medium-term (1-7d), and long-term (1-30d) predictions.',
        highlight: '3 timeframes'
      },
    ]
  },
  {
    id: 'multichain',
    label: 'Multi-Chain & DEX',
    icon: Globe,
    gradient: 'from-blue-400 to-cyan-600',
    bgGlow: 'bg-blue-400/5',
    borderGlow: 'hover:border-blue-400/30 hover:shadow-blue-400/10',
    description: 'Trade on-chain across 8 networks from one dashboard',
    features: [
      {
        icon: Globe, title: '15 Chains Supported',
        desc: 'Ethereum, BSC, Polygon, Arbitrum, Base, Avalanche, Optimism, Solana, Cronos, Fantom, zkSync Era, Mantle, Linea, Scroll, Blast. Deploy DEX bots on any chain from a single unified dashboard.',
        highlight: '15 chains'
      },
      {
        icon: Boxes, title: 'DEX Trading (Uniswap, Aerodrome, CoW, SushiSwap, Curve, +)',
        desc: 'Direct on-chain trading through Uniswap V3/V2, Aerodrome (Base), CoW Protocol, SushiSwap, Curve, Balancer, PancakeSwap, Jupiter (Solana). No centralized exchange needed — trade any token with liquidity.',
        highlight: 'On-chain native'
      },
      {
        icon: Shield, title: 'MEV-Protected Swap Router',
        desc: 'Smart router queries 0x, Paraswap, 1inch, and CoW solver network in parallel for the best route — every swap goes through MEV-protected execution to defeat sandwich bots and frontrunners.',
        highlight: 'Anti-MEV'
      },
      {
        icon: Globe, title: '15+ CEX Integrations',
        desc: 'Binance, Coinbase, Kraken, Bybit, OKX, KuCoin, Gate.io, Bitget, HTX, MEXC, Crypto.com, Bitfinex, Gemini, Bitstamp, Binance US, Poloniex — unified balance view.',
        highlight: '15+ exchanges'
      },
      {
        icon: Flame, title: 'MEXC High-Leverage Tier',
        desc: 'Up to 500x leverage on select pairs through MEXC integration. Full risk acknowledgment flow — you bring the knife, you own the cut.',
        highlight: 'Up to 500x'
      },
      {
        icon: Zap, title: 'Gas Price Optimization',
        desc: 'Real-time gas monitoring across all chains. Automatically times transactions for lowest fees and warns when gas spikes would eat your profits.',
        highlight: 'Save on gas'
      },
      {
        icon: Search, title: 'Token Auto-Discovery',
        desc: 'Automatically finds new tokens with growing liquidity and volume. Set filters for market cap, holder count, and age to discover opportunities early.',
        highlight: 'Early discovery'
      },
      {
        icon: Layers, title: 'Cross-Chain Portfolio',
        desc: 'See all positions across every chain in one view. Real-time P&L, exposure breakdown, and risk metrics — no switching between block explorers.',
        highlight: 'Unified view'
      },
      {
        icon: Lock, title: 'Non-Custodial Architecture',
        desc: 'Your keys, your crypto. haythix never holds your funds. All trades execute through your own wallets with encrypted key storage.',
        highlight: 'Your keys'
      },
    ]
  },
  {
    id: 'dex-trading-suite',
    label: 'DEX Trading Suite',
    icon: ArrowDownUp,
    gradient: 'from-cyan-400 to-blue-600',
    bgGlow: 'bg-cyan-400/5',
    borderGlow: 'hover:border-cyan-400/30 hover:shadow-cyan-400/10',
    description: 'Full bot automation on 15 chains — every CEX bot type, native on-chain',
    features: [
      {
        icon: ArrowDownUp, title: 'DEX Swap',
        desc: 'One-click MEV-protected swaps across 15 chains. Routes through CoW Protocol, 0x, Paraswap, and 1inch for best execution. Real-time gas optimization + slippage controls.',
        highlight: 'MEV-protected'
      },
      {
        icon: Target, title: 'DEX Limit Orders',
        desc: 'Place limit orders on any DEX pool without paying gas until fill. Pre-signed orders using CoW Protocol — your wallet signs once, the order rests until executed at your price.',
        highlight: 'Gasless until fill'
      },
      {
        icon: TrendingUp, title: 'DEX DCA Bot',
        desc: 'Dollar-cost averaging on-chain with configurable intervals, safety orders, take-profit triggers, and drift detection. Runs on USDC/WETH, USDC/USDT, and any DEX pair you pick.',
        highlight: 'On-chain DCA'
      },
      {
        icon: Crosshair, title: 'DEX Stop Orders',
        desc: 'Chainlink oracle-triggered stop orders. When price crosses your threshold, the bot fires a MEV-protected sell — no oracle manipulation, no flash-loan attacks. First-class stop-loss for DEX.',
        highlight: 'Chainlink oracle'
      },
      {
        icon: Boxes, title: 'DEX Smart Trade',
        desc: 'Combined limit entry + up to 5 take-profit ladders + Chainlink stop-loss as one logical position on-chain. OCO logic, break-even-after-first-TP, all signed once.',
        highlight: 'Multi-TP on-chain'
      },
      {
        icon: LayoutGrid, title: 'DEX Grid Bot',
        desc: 'Grid trading on Uniswap V3, Aerodrome (Base), SushiSwap, Curve, Balancer, PancakeSwap. Same engine as CEX grid — your choice of venue. Backtest before launch.',
        highlight: 'Native on-chain'
      },
      {
        icon: Webhook, title: 'DEX TradingView Webhooks',
        desc: 'TradingView alerts auto-execute on-chain via your wallet. Pine Script triggers → MEV-protected DEX swap → fill in seconds. HMAC-signed payloads for security.',
        highlight: 'Pine Script → DEX'
      },
      {
        icon: Timer, title: 'DEX Algo Orders (TWAP / VWAP / Iceberg)',
        desc: 'Institutional execution on-chain. TWAP time-slices, VWAP volume-weights, Iceberg hides size across multiple wallets. Previously only available on CEX or Bloomberg terminals.',
        highlight: 'Institutional execution'
      },
      {
        icon: GitBranch, title: 'DEX Conditional Bot',
        desc: 'Regime-switching orchestrator on-chain. "If BTC trends up, run DEX grid on ETH/USDC; if ranging, run DEX DCA; if down-trending, pause." 3-tick hysteresis prevents flapping.',
        highlight: 'Regime-aware DEX'
      },
      {
        icon: RefreshCw, title: 'DEX Cross-Chain Arbitrage',
        desc: 'Real-time spread detection across DEXes on different chains (Base vs Polygon, Arbitrum vs Optimism). Net profit after gas. Pre-signed cycles, MEV-protected fills, 3-loss-streak auto-pause.',
        highlight: 'Cross-chain spreads'
      },
      {
        icon: Droplets, title: 'Uniswap V3 LP Studio',
        desc: 'Manage Uniswap V3 concentrated liquidity positions — recommended ranges, fee projections, impermanent loss tracking, auto-rebalance alerts. For issuers + LPs.',
        highlight: 'LP-grade tooling'
      },
    ]
  },
  {
    id: 'community',
    label: 'Live Community',
    icon: Users,
    gradient: 'from-violet-400 to-fuchsia-600',
    bgGlow: 'bg-violet-400/5',
    borderGlow: 'hover:border-violet-400/30 hover:shadow-violet-400/10',
    badge: 'ONLY ON haythix',
    description: 'The only trading platform with a built-in social layer — chat, monetize, share verified trades',
    features: [
      {
        icon: DollarSign, title: 'Paid Channels (Creator Monetization)',
        desc: 'Channel owners can set 1-3 subscription tiers ($1-$500/mo USDC). Subscribers pay on-chain, creator gets 80% instantly to their wallet, platform takes 20%. No card, no Stripe, no KYC. Multi-chain (Base/Polygon/Arbitrum) with one-click pay.',
        highlight: '80% to creator · USDC instant'
      },
      {
        icon: Send, title: 'Trade Snapshots',
        desc: 'Share verified trade cards in chat — paste an order ID and we auto-post entry, exit, PnL, asset, and chain. Server-authoritative so traders can\'t fake gains.',
        highlight: 'Server-verified PnL'
      },
      {
        icon: Mic, title: 'Voice Notes (30s)',
        desc: 'Drop a 30-second voice note in any chat room. Perfect for hot takes during volatility when text feels too slow.',
        highlight: 'Real-time alpha'
      },
      {
        icon: Award, title: 'Top Calls of the Week',
        desc: 'Each channel auto-pins the top 3 highest-performing calls of the last 7 days. Ranked by realized % gain, computed from real on-chain prices.',
        highlight: 'Auto-leaderboard'
      },
      {
        icon: Flame, title: 'Hot Channel Badge',
        desc: '\u{1F525} HOT badge appears when a room hits 10+ messages/min. Drives FOMO clicks; surfaces the channels worth jumping into right now.',
        highlight: 'Real-time virality signal'
      },
      {
        icon: Activity, title: 'Typing Indicators',
        desc: 'See who\'s typing in real time — "Sarah is typing…" "Bob and 4 others are typing…" Makes rooms feel alive, not dead text.',
        highlight: 'Live presence'
      },
      {
        icon: CheckCircle, title: 'Tip Read Receipts',
        desc: 'Send a USDC tip and get a notification the moment the recipient sees it — "Sarah saw your $5 tip ✓". Closes the loop on appreciation.',
        highlight: '✓ Closed loop'
      },
      {
        icon: Target, title: 'Auto Trade-Call Detection',
        desc: 'Type "BUY $ETH target $3000" or "SHORT $SOL @ 200" — haythix auto-detects, captures entry price, and tracks accuracy over time. Build your reputation as a Verified Caller.',
        highlight: 'Auto-tracked'
      },
      {
        icon: BadgeCheck, title: 'Verified Caller Badge',
        desc: 'Hit 20 calls @ 60%+ accuracy and you earn the Verified Caller badge. The community sees your real track record — no more fake gurus.',
        highlight: '20 calls @ 60%+'
      },
      {
        icon: MessageCircle, title: 'Live Chat Rooms',
        desc: 'Real-time community chat organized by topic — general, altcoins, DeFi, NFTs, and more. Share ideas, ask questions, and learn from experienced traders.',
        highlight: 'Real-time'
      },
      {
        icon: Crown, title: 'Tier-Gated Rooms',
        desc: 'Exclusive rooms for Pro and Elite members. Higher signal-to-noise ratio, verified traders, and premium alpha shared by top performers.',
        highlight: 'Exclusive access'
      },
      {
        icon: Target, title: 'Verified Call Tracking',
        desc: 'Every BUY/SELL call is automatically tracked with entry price, resolution, and accuracy stats. No more fake gurus — see real verified performance.',
        highlight: 'Proof of skill'
      },
      {
        icon: ShieldCheck, title: 'Verified Track Record (Real CCXT P&L)',
        desc: 'Marketplace creators submit read-only API keys; we compute real 30-day P&L, win rate, and max drawdown from actual exchange trade history. Green badge = haythix observed it.',
        highlight: 'Anti-fake-guru'
      },
      {
        icon: Bell, title: 'Two-Way Telegram Trading',
        desc: 'Issue /buy, /sell, /closeall, /stopgrid commands directly from Telegram. 60-second confirmation window before execution. Real CCXT orders — no fake notifications.',
        highlight: 'Trade from Telegram'
      },
      {
        icon: Award, title: 'Leaderboards & Following',
        desc: '5-category leaderboards: most accurate, highest streak, most helpful, top earners, and rising stars. Follow the best and track their calls.',
        highlight: '5 categories'
      },
      {
        icon: Vote, title: 'Polls & Predictions',
        desc: 'Create community polls and market predictions. See where the crowd stands on specific tokens, market direction, or trading strategies.',
        highlight: 'Crowd wisdom'
      },
      {
        icon: Sparkles, title: 'AI Room Summaries',
        desc: 'Missed the conversation? haythix AI generates summaries of every room — top tokens mentioned, key calls, sentiment, and activity highlights.',
        highlight: 'Never miss alpha'
      },
      {
        icon: Flame, title: 'Streaks & Check-ins',
        desc: 'Daily check-in streaks with milestone badges at 7, 14, 30, 60, 90, 180, 365 days, and 1000 days. Stay engaged, build reputation, earn recognition.',
        highlight: '8 milestones'
      },
      {
        icon: ThumbsUp, title: '18 Crypto Reactions',
        desc: 'Express yourself with 18 crypto-specific reactions — from diamond hands to whale alerts. Elite members get exclusive sparkle and whale reactions.',
        highlight: '18 reactions'
      },
      {
        icon: Hash, title: '@Mentions & Notifications',
        desc: 'Tag any user with @mentions. Notification bell with unread count, reply threads, and real-time alerts when someone mentions you or replies.',
        highlight: 'Stay connected'
      },
      {
        icon: UserCheck, title: 'Token Project Verification',
        desc: 'Token projects can apply for verified status with logo, description, and proof. Admin-approved verified badges — so the community knows who is legit.',
        highlight: 'Trust layer'
      },
      {
        icon: Wallet, title: 'USDC Tip Jar',
        desc: 'Tip great traders directly with USDC through MetaMask or WalletConnect. $25 minimum with 80/20 split — creators earn real money for real alpha.',
        highlight: 'Earn from alpha'
      },
      {
        icon: Radio, title: 'Channel Discovery',
        desc: 'Browse, search, join, and create channels. Build your own community within haythix — for your token, your strategy, or your trading group.',
        highlight: 'Build your space'
      },
      {
        icon: Shield, title: 'Auto-Moderation',
        desc: '9 scam detection patterns running in real-time: pump-and-dump, fake airdrops, phishing links, impersonation, and more. Clean rooms, safe community.',
        highlight: '9 scam filters'
      },
      {
        icon: Megaphone, title: 'AI Auto-Seed Content',
        desc: 'haythix AI bot posts market updates, trading tips, and strategy breakdowns to keep rooms active. Always fresh content, even during quiet hours.',
        highlight: 'Always active'
      },
    ]
  },
  {
    id: 'crypto-payments',
    label: 'Crypto-Native Payments',
    icon: Wallet,
    gradient: 'from-emerald-400 to-cyan-500',
    bgGlow: 'bg-emerald-400/5',
    borderGlow: 'hover:border-emerald-400/30 hover:shadow-emerald-400/10',
    badge: 'NO CARD NEEDED',
    description: 'Pay for the platform with crypto. No Stripe, no card-on-file, no KYC — your wallet is the only thing you need.',
    features: [
      {
        icon: Zap, title: 'One-Click USDC Pay',
        desc: 'Subscribe to any plan with a single wallet confirmation. We pre-fill the transaction (amount, recipient, chain locked) — you just click "Confirm" in MetaMask, Rainbow, or Coinbase Wallet.',
        highlight: '~5 seconds end-to-end'
      },
      {
        icon: Globe, title: 'Multi-Chain Payments',
        desc: 'Pay from Base, Polygon, or Arbitrum — wherever your USDC already lives. Auto-detect your balance per chain, recommend the cheapest. Mainnet refused (gas too expensive).',
        highlight: '3 chains supported'
      },
      {
        icon: RefreshCw, title: 'On-Chain Auto-Renew',
        desc: 'Sign ONE allowance for 3, 6, or 12 months. We auto-debit USDC monthly via transferFrom() at renewal. You stay in custody — no card-on-file, no surprise charges, revoke any time.',
        highlight: 'Trustless auto-renew'
      },
      {
        icon: Send, title: 'USDC Tips & Subscriptions',
        desc: 'Tip creators directly with USDC. Subscribe to paid channels. 80% goes to creator instantly, 20% platform fee. Payouts confirm in under 2 seconds on Base.',
        highlight: '80% to creator'
      },
      {
        icon: Bell, title: 'Renewal Reminders',
        desc: 'T-3 day and T-1 day email + in-app reminders before any subscription expires. Click → wallet pops up with renewal pre-filled. No silent lapses.',
        highlight: '3-day + 1-day pings'
      },
      {
        icon: ShieldCheck, title: 'Server-Verified Payments',
        desc: 'Every USDC payment is verified on-chain by our backend before unlocking access — right amount, right token, right recipient. No webhook surprises, no payment confusion.',
        highlight: 'On-chain proof'
      },
    ]
  },
  {
    id: 'engineering-integrity',
    label: 'Engineering Integrity',
    icon: ShieldCheck,
    gradient: 'from-cyan-400 to-blue-500',
    bgGlow: 'bg-cyan-400/5',
    borderGlow: 'hover:border-cyan-400/30 hover:shadow-cyan-400/10',
    badge: 'NO ONE ELSE BUILDS THIS',
    description: 'Most retail bot platforms hide their math. We expose it.',
    features: [
      {
        icon: Crosshair, title: 'Position Integrity & Drift Detection',
        desc: 'Live balance verification against your exchange + on-chain wallet. Detects drift between our calculated position and reality, surfaces discrepancies, offers one-click reconciliation. Most rigorous drift handling in retail crypto.',
        highlight: 'Live reconciliation'
      },
      {
        icon: Receipt, title: 'Dual-View Accounting (FIFO/LIFO/HIFO)',
        desc: 'Two parallel ledgers: operational (per-strategy native pricing for accurate stats) AND tax (pooled FIFO/LIFO/HIFO for taxes). Switch between them — no more confusion about whether your Grid Bot "won" or "lost".',
        highlight: 'Operational + tax views'
      },
      {
        icon: Webhook, title: 'Outbound Webhooks (Discord/Telegram)',
        desc: 'Every trade event (open, close, TP fill, SL hit) fires a webhook to your Discord channel or Telegram bot. Build distribution outside haythix — your audience reaches you wherever they live.',
        highlight: 'Trade events anywhere'
      },
      {
        icon: ScanLine, title: 'Real Backtest Engine',
        desc: 'Backtest any strategy against real historical OHLCV from CCXT. No synthetic scenarios, no curve-fitting tricks. Win rate, max drawdown, Sharpe ratio — all from real exchange history.',
        highlight: 'Real history, no fakes'
      },
      {
        icon: BadgeCheck, title: 'Server-Authoritative Trade Cards',
        desc: 'When traders share trade snapshots in chat, the backend re-computes PnL from their actual trade history. No client-side fakery possible. Every shared trade is verifiable.',
        highlight: 'Anti-fake-guru'
      },
      {
        icon: AlertTriangle, title: 'No Synthetic Scenarios',
        desc: 'Our stress tests and analytics ONLY use real historical periods (LUNA collapse, FTX, COVID crash) — no "imagine if BTC drops 50%" fake scenarios. Real data over theater.',
        highlight: 'Real periods only'
      },
    ]
  },
  {
    id: 'risk',
    label: 'Risk Management',
    icon: Shield,
    gradient: 'from-emerald-400 to-green-600',
    bgGlow: 'bg-emerald-400/5',
    borderGlow: 'hover:border-emerald-400/30 hover:shadow-emerald-400/10',
    description: 'Sleep peacefully — 5 layers of protection guard your capital',
    features: [
      {
        icon: ShieldCheck, title: '5-Layer Risk Controls',
        desc: 'Per-trade limits, per-bot limits, per-wallet limits, portfolio-wide limits, and global emergency controls. Five layers between your capital and disaster.',
        highlight: '5 layers deep'
      },
      {
        icon: Wallet, title: 'Per-Wallet Spending Limits',
        desc: 'Set maximum daily/weekly spend per wallet. Even if a strategy goes rogue, it can never exceed your predefined risk budget for that wallet.',
        highlight: 'Hard caps'
      },
      {
        icon: AlertTriangle, title: 'Drawdown Protection',
        desc: 'Automatically pauses all trading when your portfolio drops below a configurable threshold. Prevents catastrophic losses during flash crashes.',
        highlight: 'Auto-protection'
      },
      {
        icon: Pause, title: 'Emergency Kill Switch',
        desc: 'One button stops everything — all bots, all chains, all wallets. Instant halt when you need it most. Resume when you are ready.',
        highlight: 'Instant stop'
      },
      {
        icon: Gauge, title: 'Volatility Auto-Pause',
        desc: 'Bots automatically pause when market volatility exceeds safe levels. No more getting liquidated during unexpected market events.',
        highlight: 'Smart pause'
      },
      {
        icon: AlertTriangle, title: 'Stress Test Library',
        desc: '8 pre-canned historical events — COVID 2020, China ban 2021, LUNA collapse, FTX, August 2024 carry unwind, April 2025 tariff panic. Run your strategy through real OHLCV from the worst weeks in crypto.',
        highlight: '8 historical events'
      },
      {
        icon: Sparkles, title: 'Leverage Tier Warnings',
        desc: '4-tier leverage classification with automatic risk callouts. Conservative (1-10x) → Aggressive (10-50x) → Extreme (50-125x) → MEXC-Only (125x+) with explicit "industry data shows >95% liquidation rate at this leverage" warnings.',
        highlight: 'Risk-aware UI'
      },
      {
        icon: Play, title: 'Guided Onboarding Wizard',
        desc: 'First-login 5-step wizard: connect exchange → pick risk profile → choose starter strategy → seed first bot → enable notifications. Skippable, resumable from dashboard.',
        highlight: 'Zero confusion'
      },
      {
        icon: Crosshair, title: 'Stop-Loss & Take-Profit',
        desc: 'Configurable stop-loss and take-profit on every trade. Trailing stops follow price action to lock in gains while limiting downside.',
        highlight: 'Trailing stops'
      },
      {
        icon: Bell, title: 'Multi-Channel Alerts',
        desc: 'Get notified via in-app, email, SMS, Telegram, and Discord. Know what your bots are doing at all times — on any device, any platform.',
        highlight: '5 channels'
      },
    ]
  },
  {
    id: 'verticals',
    label: 'Signature Verticals',
    icon: ShieldCheck,
    gradient: 'from-emerald-400 to-emerald-600',
    bgGlow: 'bg-emerald-400/5',
    borderGlow: 'hover:border-emerald-400/40 hover:shadow-emerald-400/15',
    badge: 'UNIQUE TO haythix',
    description: 'Seven depth moats — every claim backed by a public API endpoint',
    features: [
      {
        icon: ShieldCheck, title: 'Vertical 1 — Wallet-as-Bot',
        desc: 'Non-custodial automation foundation. EIP-712 signed session policy declares hard caps ($100k/trade, $1M/session, 30-day max) and a forbidden-operations floor (setApprovalForAll, approve_unlimited, transfer, permit2_unlimited, execute_arbitrary). Even a malicious frontend cannot raise these caps. Public endpoint: GET /api/wallet-sessions/policy.',
        highlight: 'Public policy endpoint'
      },
      {
        icon: Target, title: 'Vertical 2 — Forecast Accuracy Track Record',
        desc: 'Every AI forecast we publish is logged with its prediction horizon, then resolved against real OHLCV. Wilson 95% confidence interval on real outcomes only — neutral moves under 0.1% excluded so we never claim credit for noise. Sample size always visible; small-n forecasts automatically de-ranked. Public endpoint: GET /api/forecast-accuracy/summary.',
        highlight: 'Wilson 95% CI'
      },
      {
        icon: Radio, title: 'Vertical 3 — Verified Creator + Signal Channels',
        desc: 'Verified-caller badge requires 20+ decided calls AND 60%+ accuracy AND 30+ days active — never partially. Leaderboard sorted by Wilson lower bound, so a careful 50-call creator outranks a lucky 3-call one. Personal inbox; rejected self-subscribe; dual-indexed for fast distribution. Public endpoint: GET /api/verified-creators/policy.',
        highlight: 'Stats over luck'
      },
      {
        icon: Receipt, title: 'Vertical 4 — Tax-Loss Harvest + What-If Scenarios',
        desc: 'FIFO lots reconstructed from your actual bot fills. Stable-quote-only USD anchoring (volatile-quote pairs explicitly skipped). Conservative replacement map (ETH→WSTETH, BTC→WBTC, SOL→MSOL). What-if Sharpe / VaR / max drawdown over real Binance daily OHLCV; assets without history surfaced as skipped, not imputed. Public endpoint: GET /api/tax-strategy/replacement-map.',
        highlight: 'No imputed prices'
      },
      {
        icon: Building2, title: 'Vertical 5 — Tokenomics-as-a-Service B2B',
        desc: 'Issuer workspace: multi-token org, live holder analysis from real chain explorers (Gini, top-10/top-50 concentration), AMM unlock-impact simulator, public investor report URL, and SHA256-hashed API keys (plaintext shown ONCE). The investor report never exposes userId, members, key hashes, or unlock notes. Public endpoint: GET /api/tokenomics/r/{publicSlug}.',
        highlight: 'B2B + public reports'
      },
      {
        icon: Compass, title: 'Vertical 7 — Token Universe Scanner',
        desc: 'Cross-chain scanner that surfaces tokens matching user-defined criteria (volume spike, price breakout, liquidity floor, price-change band, age range). Every run discloses universe stats (total considered / included / excluded by reason), partial-run reasons when chains fail or data is missing, full per-criterion score breakdown with the formula used, and a SHA-256 methodology hash so any two runs of the same definition produce the same hash. Explicit empty states (no imputed data) and 365-day audit trail. Public endpoint: GET /api/scanner/policy.',
        highlight: 'Methodology hash + universe stats'
      },
    ]
  },
  {
    id: 'elite',
    label: 'Elite Suite',
    icon: Crown,
    gradient: 'from-amber-300 via-yellow-400 to-orange-500',
    bgGlow: 'bg-amber-400/5',
    borderGlow: 'hover:border-amber-400/40 hover:shadow-amber-400/15',
    badge: 'ELITE TIER',
    description: '17 institutional-grade tools that exist nowhere else in crypto trading. Organized into Trader\'s Suite, Issuer Tools, and Tokenomics.',
    features: [
      // ─── Trader's Suite — pro-trader power tooling ─────────────────────────
      {
        icon: Layers, title: 'Multi-Account Aggregator',
        desc: 'Unify positions across all your connected exchanges and wallets into one consolidated view. Track combined P&L, total exposure per asset, and cross-venue arbitrage opportunities — without manually summing 7 spreadsheets.',
        highlight: 'All accounts, one view'
      },
      {
        icon: Receipt, title: 'Tax-Loss Harvest Engine',
        desc: 'Auto-detects realized loss opportunities in your portfolio — surfaces which positions to sell now to offset gains. Configurable wash-sale guard (30/60 days). Integrates with the dual-view tax ledger for FIFO/LIFO/HIFO compliance.',
        highlight: 'Save thousands at tax time'
      },
      {
        icon: FileText, title: 'Institutional Reports',
        desc: 'Bloomberg-grade PDF reports: monthly performance, risk attribution, drawdown analysis, factor exposure, tax summary. Audit-ready for accountants and LPs. Generate in one click, brand with your firm logo.',
        highlight: 'PDF + audit-ready'
      },
      {
        icon: Brain, title: 'AI Strategy Coach',
        desc: 'Chat with haythix AI about your actual portfolio, positions, and strategy. Personalized risk assessments grounded in YOUR trading data — not generic crypto advice. Catches over-concentration, suggests hedges, flags drawdown risk before it happens.',
        highlight: 'Grounded in your data'
      },
      {
        icon: FlaskConical, title: 'Quant Lab',
        desc: 'Code-free quant research environment. Test hypotheses against real CCXT OHLCV across 15 exchanges. Build factor models, regression analyses, signal correlations. Export findings as strategy templates for direct deployment.',
        highlight: 'Quant research, no code'
      },
      {
        icon: Droplets, title: 'Uniswap V3 LP Studio',
        desc: 'Provide concentrated liquidity on Uniswap V3 with confidence. Range optimizer based on historical volatility, IL calculator, fee-vs-IL break-even, position rebalancer. Manage LP positions like a pro market maker.',
        highlight: 'LP like a market maker'
      },
      {
        icon: ShieldAlert, title: 'Token Launch Scanner',
        desc: '24/7 monitoring of new token launches across 7 EVM chains. Honeypot detection, liquidity locks, contract verification, owner privileges. Filter by chain, liquidity, age. Snipe legit launches; avoid scams.',
        highlight: 'Avoid 99% of scams'
      },

      // ─── Issuer Tools — for token projects ─────────────────────────────────
      {
        icon: Droplets, title: 'DEX Liquidity Optimizer',
        desc: 'Compute the exact Uniswap V2/V3 liquidity needed to support your target daily volume at chosen slippage. Real math, no estimates. Tells issuers precisely how much capital to seed.',
        highlight: 'Real math, no guesses'
      },
      {
        icon: ClipboardCheck, title: 'Listing Readiness Scorecard',
        desc: 'Score your token against the public listing criteria of Binance, Coinbase, OKX, Kraken, and Gemini. On-chain data + CoinGecko + self-attestation. See exactly what\'s missing before you apply.',
        highlight: '5 exchanges scored'
      },
      {
        icon: TrendingUp, title: 'Launch Volume Forecaster',
        desc: 'Forecast realistic organic daily volume for a token launch using real CoinGecko comparables — similar projects\' first-month volume, market caps, exchange listings, holder distribution.',
        highlight: 'Real comparables'
      },
      {
        icon: Timer, title: 'Launch Roadmap Planner',
        desc: 'Plan token launches across phased timelines — marketing pushes, exchange listings, audits, partnerships, treasury moves, community goals. 4 pre-built templates: Token Launch, Listing Push, Community Growth, Liquidity Migration.',
        highlight: '4 launch templates'
      },
      {
        icon: Gauge, title: 'Volume Health Widget (embeddable)',
        desc: 'Embeddable SVG badge for your project site showing real token health. 6-factor analysis: volume consistency, unique traders, buy/sell balance, liquidity, holders, organic patterns. Updates hourly.',
        highlight: 'Embed anywhere'
      },
      {
        icon: Radar, title: 'Competitor Activity Tracker',
        desc: 'Monitor rival tokens with bot detection (5 indicators), daily volume charts, hourly activity patterns, dominant wallet analysis. See exactly what your competitors are doing — and what\'s working.',
        highlight: 'Spy on rivals legally'
      },

      // ─── Tokenomics suite ─────────────────────────────────────────────────
      {
        icon: FileText, title: 'AI Tokenomics Advisor',
        desc: 'Upload a whitepaper PDF or paste tokenomics text — haythix AI extracts supply, distribution, vesting schedules, risks, and generates a phased strategy. Compares against best-in-class examples.',
        highlight: 'PDF analysis'
      },
      {
        icon: Building2, title: 'Tokenomics Studio',
        desc: 'Build your tokenomics from scratch with a guided workflow. Supply curves, vesting schedules, allocation breakdown, incentive flows. Stress-test against demand scenarios. Export to whitepaper-ready PDF.',
        highlight: 'Whitepaper-ready output'
      },

      // ─── Exclusive Elite-only community + access ──────────────────────────
      {
        icon: Crown, title: 'Elite Lounge + Whale Club',
        desc: 'Private chat rooms only Elite members can enter. Direct founder access, weekly AMAs, private alpha calls, early feature previews. Get reactions and badges no one else has.',
        highlight: 'Direct founder access'
      },
      {
        icon: Sparkles, title: 'Elite-Only Reactions + Badges',
        desc: 'Exclusive Elite-tier reactions (sparkle, whale) and badge cosmetics that signal status across the community. Other users can see you\'re Elite — recognition is part of the package.',
        highlight: 'Visible status'
      },
    ]
  },
];

// ─── Total feature count ────────────────────────────────────────────────────
//
// PLATFORM_FEATURE_COUNT is the canonical platform total (147+) — comes from the
// Comparison page's SECTIONS catalog. SHOWCASE_COUNT is the number of feature
// cards rendered on THIS page (a curated highlight, not the full inventory).
// User-facing copy uses PLATFORM_FEATURE_COUNT so the number is consistent
// site-wide.

const TOTAL_FEATURES = PLATFORM_FEATURE_COUNT;
const SHOWCASE_COUNT = CATEGORIES.reduce((sum, cat) => sum + cat.features.length, 0);

// ─── 3D Card component ─────────────────────────────────────────────────────

function Feature3DCard({ feature, gradient, index }) {
  return (
    <div
      className="group relative perspective-1000"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="relative bg-[#0d0e14] border border-white/[0.06] rounded-2xl p-5 transition-all duration-500
        hover:border-white/[0.12] hover:-translate-y-2 hover:shadow-2xl hover:shadow-black/40
        transform-gpu hover:rotate-x-1 hover:rotate-y-1"
        style={{
          transformStyle: 'preserve-3d',
          backfaceVisibility: 'hidden',
        }}
      >
        {/* Glow effect on hover */}
        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-[0.04] transition-opacity duration-500 pointer-events-none`} />

        {/* Top row: icon + highlight badge */}
        <div className="flex items-center justify-between mb-3 relative z-10">
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center
            shadow-lg group-hover:scale-110 transition-transform duration-300`}
            style={{ transform: 'translateZ(20px)' }}
          >
            <feature.icon className="w-5 h-5 text-white" />
          </div>
          {feature.highlight && (
            <span className="text-[9px] font-bold px-2.5 py-1 rounded-full bg-white/[0.06] text-gray-400 tracking-wider uppercase
              group-hover:bg-white/[0.1] group-hover:text-white transition-all duration-300">
              {feature.highlight}
            </span>
          )}
        </div>

        {/* Content */}
        <h3 className="font-semibold text-white mb-2 text-[15px] relative z-10">{feature.title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed relative z-10 group-hover:text-gray-400 transition-colors duration-300">
          {feature.desc}
        </p>

        {/* Bottom shine line */}
        <div className={`absolute bottom-0 left-4 right-4 h-[1px] bg-gradient-to-r ${gradient} opacity-0 group-hover:opacity-30 transition-opacity duration-500`} />
      </div>
    </div>
  );
}

// ─── Category nav pill ──────────────────────────────────────────────────────

function CategoryPill({ cat, active, onClick }) {
  const Icon = cat.icon;
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 whitespace-nowrap ${
        active
          ? `bg-gradient-to-r ${cat.gradient} text-gray-900 shadow-lg`
          : 'bg-white/[0.04] text-gray-400 hover:bg-white/[0.08] hover:text-white border border-white/[0.06]'
      }`}
    >
      <Icon className="w-4 h-4" />
      {cat.label}
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
        active ? 'bg-black/20 text-gray-900' : 'bg-white/[0.06] text-gray-500'
      }`}>
        {cat.features.length}
      </span>
    </button>
  );
}

// ─── Page component ─────────────────────────────────────────────────────────

export default function FeaturesPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('trading');
  const activeCat = CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div className="min-h-screen bg-[#0a0b0f] text-white">
      <Seo
        title={`${TOTAL_FEATURES} features for DEX & CEX traders, copy traders & token issuers`}
        description={`${TOTAL_FEATURES} features across ${CATEGORIES.length} categories: trading bots, copy trading, AI signals, multi-chain arbitrage, tokenomics tools, and tax reports. Built for serious crypto traders.`}
        path="/features"
      />

      {/* ── Nav ── */}
      <PublicNav />

      {/* ── Hero ── */}
      <div className="text-center pt-16 pb-8 px-4">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-amber-400/10 to-violet-400/10 border border-amber-400/20 mb-6">
          <Sparkles className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-semibold text-amber-400 tracking-wide">{TOTAL_FEATURES} FEATURES ACROSS {CATEGORIES.length} CATEGORIES</span>
        </div>
        <h1 className="text-4xl md:text-6xl font-bold mb-5">
          <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-violet-400 bg-clip-text text-transparent">
            Every feature
          </span>
          {' '}a trader needs
        </h1>
        <p className="text-gray-400 text-lg max-w-3xl mx-auto mb-4">
          Trading bots, AI intelligence, multi-chain DEX, live community, risk management, and Elite-exclusive tools —
          all in one platform. No other platform comes close.
        </p>
        <p className="text-sm text-gray-500 max-w-2xl mx-auto">
          Every feature below is <span className="text-gray-300">live and working</span> — not a roadmap, not "coming soon."
          Sign up today and use them all.
        </p>
      </div>

      {/* ── Category stats bar ── */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`relative rounded-xl p-3 text-center transition-all duration-300 border ${
                  isActive
                    ? `bg-gradient-to-br ${cat.gradient} bg-opacity-10 border-white/10 shadow-lg`
                    : 'bg-white/[0.02] border-white/[0.04] hover:bg-white/[0.05] hover:border-white/[0.08]'
                }`}
              >
                <Icon className={`w-5 h-5 mx-auto mb-1.5 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                <p className={`text-2xl font-bold ${isActive ? 'text-white' : 'text-gray-400'}`}>
                  {cat.features.length}
                </p>
                <p className={`text-[10px] font-medium tracking-wide ${isActive ? 'text-white/80' : 'text-gray-600'}`}>
                  {cat.label}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Category navigation pills ── */}
      <div className="max-w-7xl mx-auto px-4 pb-6">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {CATEGORIES.map(cat => (
            <CategoryPill
              key={cat.id}
              cat={cat}
              active={activeCategory === cat.id}
              onClick={() => setActiveCategory(cat.id)}
            />
          ))}
        </div>
      </div>

      {/* ── Active category header ── */}
      <div className="max-w-7xl mx-auto px-4 pb-6">
        <div className={`rounded-2xl border border-white/[0.06] p-6 md:p-8 ${activeCat.bgGlow}`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${activeCat.gradient} flex items-center justify-center`}>
                  <activeCat.icon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold">{activeCat.label}</h2>
                {activeCat.badge && (
                  <span className={`text-[9px] font-bold px-2.5 py-1 rounded-full tracking-wider ${
                    activeCat.badge === 'ELITE TIER'
                      ? 'bg-gradient-to-r from-amber-400/20 to-orange-400/20 text-amber-400 border border-amber-400/25'
                      : 'bg-violet-400/15 text-violet-400 border border-violet-400/25'
                  }`}>
                    {activeCat.badge}
                  </span>
                )}
              </div>
              <p className="text-gray-400">{activeCat.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-4xl font-bold bg-gradient-to-r ${activeCat.gradient} bg-clip-text text-transparent`}>
                {activeCat.features.length}
              </span>
              <span className="text-sm text-gray-500">features</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Feature cards grid ── */}
      <div className="max-w-7xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {activeCat.features.map((feature, i) => (
            <Feature3DCard
              key={feature.title}
              feature={feature}
              gradient={activeCat.gradient}
              index={i}
            />
          ))}
        </div>
      </div>

      {/* ── "All categories at a glance" section ── */}
      <div className="bg-[#0d0e14] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">
            All {TOTAL_FEATURES} features at a glance
          </h2>
          <p className="text-gray-400 text-center mb-10 max-w-xl mx-auto">
            Tap any category to explore. Every feature is live and included in your plan.
          </p>

          <div className="space-y-4">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => { setActiveCategory(cat.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className={`w-full rounded-2xl border border-white/[0.06] p-5 flex items-center gap-4
                    hover:border-white/[0.12] transition-all duration-300 group text-left ${cat.bgGlow}`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${cat.gradient} flex items-center justify-center
                    flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-white">{cat.label}</h3>
                      {cat.badge && (
                        <span className={`text-[8px] font-bold px-2 py-0.5 rounded-full tracking-wider ${
                          cat.badge === 'ELITE TIER'
                            ? 'bg-amber-400/15 text-amber-400 border border-amber-400/25'
                            : 'bg-violet-400/15 text-violet-400 border border-violet-400/25'
                        }`}>
                          {cat.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">{cat.description}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <span className={`text-2xl font-bold bg-gradient-to-r ${cat.gradient} bg-clip-text text-transparent`}>
                        {cat.features.length}
                      </span>
                      <p className="text-[10px] text-gray-600">features</p>
                    </div>
                    <div className="flex -space-x-1">
                      {cat.features.slice(0, 4).map((f, i) => {
                        const FIcon = f.icon;
                        return (
                          <div key={i} className={`w-7 h-7 rounded-lg bg-gradient-to-br ${cat.gradient} flex items-center justify-center
                            border-2 border-[#0d0e14] opacity-60`}>
                            <FIcon className="w-3 h-3 text-white" />
                          </div>
                        );
                      })}
                      {cat.features.length > 4 && (
                        <div className="w-7 h-7 rounded-lg bg-white/[0.06] flex items-center justify-center
                          border-2 border-[#0d0e14] text-[9px] text-gray-500 font-bold">
                          +{cat.features.length - 4}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="max-w-4xl mx-auto px-4 py-20">
        <div className="rounded-2xl bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-violet-500/10 border border-amber-400/20 p-8 md:p-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/20 mb-4">
            <Crown className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[10px] font-bold text-amber-400 tracking-wider">FOUNDERS PROGRAM — 50% OFF FOREVER</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            {TOTAL_FEATURES} features. One platform. One price.
          </h2>
          <p className="text-gray-400 mb-6 max-w-lg mx-auto">
            This page shows {SHOWCASE_COUNT} highlights from our full {TOTAL_FEATURES}-feature catalog — see every one of them, head-to-head against {5} competitors, on the <Link to="/comparison" className="text-amber-300 hover:text-amber-200 underline underline-offset-2">Comparison page</Link>.
            Start with a 14-day free trial or lock in Founders pricing — 50% off forever for the first 100 subscribers.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate('/comparison')}
              className="px-6 py-3 rounded-xl text-sm font-medium text-gray-300 border border-dark-600 hover:text-white hover:border-dark-500 transition-colors flex items-center gap-2"
            >
              Compare vs Competitors <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/register?plan=trial')}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-gray-900 font-semibold text-sm hover:from-amber-300 hover:to-orange-300 transition-all flex items-center gap-2"
            >
              Start 14-Day Free Trial <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <PublicFooter />

      {/* ── Custom CSS for 3D effects ── */}
      <style>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .hover\\:rotate-x-1:hover {
          transform: rotateX(1deg) rotateY(1deg) translateY(-8px);
        }
        .hover\\:rotate-y-1:hover {
          transform: rotateX(1deg) rotateY(1deg) translateY(-8px);
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
